import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import { locations } from '../data/locationData';

const locationSeoData = locations.reduce((acc, loc) => {
  acc[`/${loc.slug}`] = {
    title: loc.metaTitle,
    description: loc.metaDescription,
    keywords: loc.metaKeywords,
  };
  return acc;
}, {});

const SEO_DATA = {
  '/': {
    title: 'Trika Studio | Web Development & Design — Triangle, NC',
    description: 'Trika Studio — Web development, branding, and digital solutions for startups and growing businesses. Based in the Triangle, NC — serving clients nationwide. Fixed pricing. Fast delivery. Senior-level expertise.',
    keywords: 'web development, web design, branding, Triangle NC, Raleigh Durham, startup website, small business website, fixed price web development, nationwide web developer'
  },
  '/services': {
    title: 'Web Development Services | Trika Studio — Serving Clients Nationwide',
    description: 'Professional web development, branding, AI-powered tools, and monthly support for startups and small businesses. Fixed pricing starting at $1,200. Based in the Triangle, NC — serving clients across the US.',
    keywords: 'web development services, branding services, AI tools, monthly website support, Triangle NC web developer, remote web developer, nationwide'
  },
  '/pricing': {
    title: 'Transparent Fixed Pricing | Trika Studio — No Hidden Fees',
    description: 'Clear, fixed-price web development packages. Starter websites from $1,200, business websites from $3,800, web applications from $9,500. No hourly billing, no surprises.',
    keywords: 'web development pricing, fixed price website, affordable web design, website cost, affordable web developer'
  },
  '/work': {
    title: 'Portfolio & Case Studies | Trika Studio — Real Client Results',
    description: 'See real projects built by Trika Studio. From the Triangle Crokinole Club to HRally and Buzzy Gardner Law — delivering results for startups and small businesses.',
    keywords: 'web development portfolio, website case studies, client projects, web design examples, startup websites'
  },
  '/about': {
    title: 'About Anushka | Trika Studio — NCAA Athlete Turned Developer',
    description: 'Meet Anushka — NCAA Division I athlete turned web developer with 10+ years of experience. I built Trika Studio to deliver senior-level web development without the agency markup. Based in Triangle, NC.',
    keywords: 'about Trika Studio, Anushka web developer, NCAA athlete developer, Triangle NC freelance developer, remote web developer'
  },
  '/contact': {
    title: 'Start Your Project | Contact Trika Studio — Free Discovery Call',
    description: 'Get in touch with Trika Studio. Book a free 30-minute discovery call or send a message. Based in the Triangle, NC — serving clients nationwide. Call (919) 229-5525.',
    keywords: 'contact web developer, hire web developer, free consultation, book discovery call, web development quote, remote web developer'
  },
  '/blog': {
    title: 'Blog | Trika Studio — Web Development Tips & Business Advice',
    description: 'Practical advice on web development, design, and digital strategy for small businesses and startups. No fluff, no jargon — just honest guidance.',
    keywords: 'web development blog, small business website tips, web design advice, startup website guide, how much does a website cost'
  },
  '/privacy': {
    title: 'Privacy Policy | Trika Studio — Your Privacy Matters',
    description: 'Learn how Trika Studio collects, uses, and protects your personal information. GDPR and CCPA compliant privacy policy.',
    keywords: 'privacy policy, data protection, GDPR, CCPA, personal information, data privacy'
  },
  '/terms': {
    title: 'Terms of Service | Trika Studio — Service Agreement',
    description: 'Terms and conditions governing the use of Trika Studio\'s web development services. Clear, transparent service agreements.',
    keywords: 'terms of service, service agreement, terms and conditions, web development contract, legal terms'
  },
  '/digital-marketing': {
    title: 'Digital Marketing Services | SEO, PPC, Social Media — Trika Studio',
    description: 'Get more customers with digital marketing from Trika Studio. SEO, Google Ads, social media, and content marketing — all managed by one expert. No agency overhead.',
    keywords: 'digital marketing services, online marketing, small business marketing, digital marketing agency, SEO services, PPC management, social media marketing, content marketing'
  },
  '/seo': {
    title: 'SEO Services | Local & National SEO — Trika Studio',
    description: 'Rank higher on Google with proven SEO strategies. Local SEO, national SEO, technical SEO, and content optimization. Transparent pricing starting at $500/mo.',
    keywords: 'SEO services, local SEO, national SEO, search engine optimization, Google ranking, small business SEO, SEO agency, SEO company, keyword ranking'
  },
  '/google-ads': {
    title: 'Google Ads Management | PPC Advertising — Trika Studio',
    description: 'Get leads today with Google Ads. Expert PPC management, landing page optimization, and transparent reporting. No long-term contracts. Management from $500/mo.',
    keywords: 'Google Ads management, PPC management, pay per click advertising, Google Ads agency, PPC agency, Google Ads for small business, SEM, search engine marketing'
  },
  '/social-media-marketing': {
    title: 'Social Media Marketing & Management — Trika Studio',
    description: 'Build your brand on social media. Content creation, scheduling, community management, and paid social campaigns. Plans starting at $750/mo.',
    keywords: 'social media marketing, social media management, Facebook marketing, Instagram marketing, LinkedIn marketing, social media agency, social media for small business'
  },
  '/content-marketing': {
    title: 'Content Marketing Services | Blog Writing & Email — Trika Studio',
    description: 'Attract customers with SEO blog posts, email campaigns, and lead magnets. Content marketing that ranks on Google and converts visitors to leads. From $800/mo.',
    keywords: 'content marketing services, blog writing services, SEO content, email marketing, lead magnets, content strategy, blog writing agency, content creation'
  },
  '/law-firm-marketing': {
    title: 'Law Firm Marketing | SEO, Google Ads & Websites for Lawyers — Trika Studio',
    description: 'Get more clients for your law firm. SEO, Google Ads, and content marketing designed for attorneys. Better than FindLaw — you own everything. No long-term contracts.',
    keywords: 'law firm marketing, lawyer marketing, attorney marketing, SEO for lawyers, Google Ads for attorneys, law firm website, legal marketing, FindLaw alternative, lawyer SEO'
  },
  '/medical-practice-marketing': {
    title: 'Medical Practice Marketing | SEO & Google Ads for Doctors — Trika Studio',
    description: 'Get more patients for your medical practice. HIPAA-aware marketing: SEO, Google Ads, content, and reputation management for doctors and healthcare providers.',
    keywords: 'medical practice marketing, healthcare marketing, doctor marketing, medical SEO, Google Ads for doctors, patient acquisition, medical website, healthcare SEO, dentist marketing'
  }
};

export const SEO = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    
    // Check if this is a blog post page
    let seo;
    if (path.startsWith('/blog/')) {
      const slug = path.replace('/blog/', '');
      const post = blogPosts.find(p => p.slug === slug);
      if (post) {
        seo = {
          title: `${post.title} | Trika Studio Blog`,
          description: post.metaDescription,
          keywords: post.metaKeywords
        };
      } else {
        seo = SEO_DATA['/blog'] || SEO_DATA['/'];
      }
    } else {
      seo = SEO_DATA[path] || locationSeoData[path] || SEO_DATA['/'];
    }

    document.title = seo.title;

    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    updateMeta('description', seo.description);
    updateMeta('keywords', seo.keywords);

    updateMeta('og:title', seo.title, true);
    updateMeta('og:description', seo.description, true);
    updateMeta('og:url', `https://trikastudio.com${path}`, true);

    updateMeta('twitter:title', seo.title);
    updateMeta('twitter:description', seo.description);
    updateMeta('twitter:url', `https://trikastudio.com${path}`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://trikastudio.com${path === '/' ? '/' : path}`);

    // Track page view in GA4
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: seo.title,
        page_location: window.location.href,
        page_path: path
      });
    }
  }, [location]);

  return null;
};
